import { useRef, useState } from "react";
import { startAiCompareStream } from "../../../api/compareApi";
import type { Product } from "../../../types/product";

export const useAiCompareSSE = () => {
  const [blocks, setBlocks] = useState<string[]>([]);
  const [liveBlock, setLiveBlock] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const abortRef = useRef<AbortController | null>(null);
  const liveLinesRef = useRef<string[]>([]);

  const syncLiveBlock = () => {
    setLiveBlock(liveLinesRef.current.join("\n"));
  };

  const commitLiveBlock = () => {
    if (liveLinesRef.current.length === 0) return;

    const block = liveLinesRef.current.join("\n");
    liveLinesRef.current = [];

    if (!block.trim()) {
      setLiveBlock("");
      return;
    }

    setBlocks((prev) => [...prev, block]);
    setLiveBlock("");
  };

  const startCompare = async (
    selectedIds: string[],
    allProducts: Product[]
  ) => {
    abortRef.current?.abort();

    const selectedProducts = allProducts.filter((p) =>
      selectedIds.includes(p.id)
    );

    if (selectedProducts.length < 2) {
      alert("比較する商品を2つ以上選んでください");
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;

    liveLinesRef.current = [];
    setBlocks([]);
    setLiveBlock("");
    setDone(false);
    setLoading(true);

    try {
      await startAiCompareStream(
        selectedProducts,
        (chunk: string) => {
          const line = chunk.replace(/\r\n/g, "\n");

          console.log("RAW CHUNK >>>", line);

          // 空行で段落確定
          if (line === "" || line === "\n") {
            commitLiveBlock();
            return;
          }

          // 区切り線は単独ブロック
          if (line.trim() === "---") {
            commitLiveBlock();
            setBlocks((prev) => [...prev, "---"]);
            return;
          }

          // 1 chunk = 1行としてそのまま保持
          liveLinesRef.current.push(line);
          syncLiveBlock();
        },
        () => {
          commitLiveBlock();
          setLoading(false);
          setDone(true);
        },
        (e: unknown) => {
          console.error("ai-compare error", e);
          commitLiveBlock();
          setLoading(false);
        },
        controller.signal
      );
    } catch (e) {
      console.error("ai-compare failed", e);
      commitLiveBlock();
      setLoading(false);
    }
  };

  const stopCompare = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    liveLinesRef.current = [];
    setBlocks([]);
    setLiveBlock("");
    setLoading(false);
    setDone(false);
  };

  return {
    blocks,
    liveBlock,
    loading,
    done,
    startCompare,
    stopCompare,
  };
};